import { useState, useEffect, useRef } from "react"
import { AlertCircle } from "lucide-react"
import { useNavigate } from "react-router-dom"
import axiosInstance from "../../../api/Axiosinstance"
import { useToast } from "../../../hooks/useToast"

const emailRegex = /\S+@\S+\.\S+/

export default function ForgetPassword() {
  const [email, setEmail] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const redirectTimer = useRef(null)
  const toast = useToast()

  useEffect(() => {
    return () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current)
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const normalizedEmail = email.trim().toLowerCase()
    if (!normalizedEmail || !emailRegex.test(normalizedEmail)) {
      setError("Incorrect email address")
      return
    }
    setError("")
    setLoading(true)

    try {
      await axiosInstance.post("/auth/forgot-password", { email: normalizedEmail })
      toast.success("Reset link sent! Redirecting to login...")
      redirectTimer.current = setTimeout(() => navigate("/auth/login"), 3000)
    } catch (err) {
      toast.apiError(err, "Unable to send reset email.")
    } finally {
      setLoading(false)
    }
  }

  const inputBase =
    "w-full rounded-lg border px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none transition focus:ring-2 focus:ring-offset-0"
  const errorInput = "border-red-300 focus:border-red-500 focus:ring-red-200"
  const normalInput = "border-gray-300 focus:border-[#7a0051] focus:ring-[#7a0051]/30 shadow-sm"

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-4 text-gray-900">
      <div className="w-full max-w-md space-y-8 text-center">
        <div className="flex justify-center">
          <img src="/images/logo.png" alt="PULO Logo" className="w-20" />
        </div>
        <div className="space-y-2">
          <h1 className="text-4xl font-semibold">Forgot Password?</h1>
          <p className="text-md font-medium text-gray-600">
            Enter your registered email address and we will send you password reset instructions
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-left">
          <div>
            <label className="mb-2 block text-sm text-gray-800">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (error) setError("")
              }}
              placeholder="abdulsimj5@gmail.com"
              className={`${inputBase} placeholder:text-gray-500 placeholder:font-medium ${
                error ? errorInput : normalInput
              }`}
            />
            {error && (
              <p className="mt-2 flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full rounded-lg py-3 text-base font-semibold text-white shadow-md transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7a0051] ${
              loading ? "bg-gray-400 cursor-not-allowed" : "bg-[#61013B] hover:bg-[#640140]"
            }`}
          >
            {loading ? "Sending..." : "Reset Password"}
          </button>
        </form>

        <div className="pt-2">
          <button
            type="button"
            onClick={() => navigate("/auth/login")}
            className="text-sm font-semibold text-[#7a0051] underline underline-offset-4 hover:text-[#640140]"
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  )
}
